/**
 * SendCompAuditDialog — admin/editor tool: gift a completed audit to
 * a prospect via magic link.
 *
 * Opens from an admin-only "Send comp audit" button on the analysis
 * page. Takes email + optional name + optional personal note, calls
 * POST /admin/analyses/:id/send-comp-audit, then shows a success toast
 * with the Resend message id for quick verification.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';

export function SendCompAuditDialog({
  analysisId,       // when set: gift THIS specific analysis
  userId,           // when set: gift the target user's LATEST completed analysis
  profileDisplay,
  defaultEmail = '',
  defaultName = '',
  open,
  onOpenChange,
  onSent,
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [name, setName] = useState(defaultName);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Reset defaults each time the dialog opens so re-opening for a
  // different row doesn't leak the previous prefill.
  useEffect(() => {
    if (open) {
      setEmail(defaultEmail);
      setName(defaultName);
      setMessage('');
    }
  }, [open, defaultEmail, defaultName]);

  const reset = () => {
    setEmail(defaultEmail);
    setName(defaultName);
    setMessage('');
    setSending(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = (email || '').trim().toLowerCase();
    if (!clean || !/^\S+@\S+\.\S+$/.test(clean)) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      const endpoint = analysisId
        ? `/admin/analyses/${analysisId}/send-comp-audit`
        : `/admin/users/${userId}/send-comp-audit-latest`;
      const { data } = await api.post(endpoint, {
        email: clean,
        name: name.trim() || undefined,
        message: message.trim() || undefined,
      });
      const flavor = data?.created_new_user ? 'created + sent' : 'sent';
      toast.success(`Comp audit ${flavor} to ${clean}`, {
        description: data?.resend_message_id ? `Resend id: ${data.resend_message_id}` : 'Email queued.',
      });
      onSent?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Send failed';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!sending) onOpenChange(next); }}>
      <DialogContent className="sm:max-w-lg" data-testid="send-comp-audit-dialog">
        <DialogHeader>
          <DialogTitle>Send complimentary audit</DialogTitle>
          <DialogDescription>
            Create an account for the recipient and email them a one-click magic link to view
            {profileDisplay ? <> the audit for <b>{profileDisplay}</b></> : <> this audit</>}. Link expires in 7 days.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="cca-email">Recipient email <span className="text-red-500">*</span></Label>
            <Input
              id="cca-email"
              type="email"
              autoFocus
              autoComplete="off"
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="send-comp-audit-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cca-name">Recipient name <span className="text-gray-400 text-xs">(optional)</span></Label>
            <Input
              id="cca-name"
              type="text"
              autoComplete="off"
              placeholder="First name for the email greeting"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="send-comp-audit-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cca-msg">Personal note <span className="text-gray-400 text-xs">(optional)</span></Label>
            <Textarea
              id="cca-msg"
              rows={3}
              placeholder="Hi Priya — thought this might be useful for your Q1 push. Happy to jump on a call once you've had a look."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="resize-none"
              data-testid="send-comp-audit-message"
            />
          </div>
          <DialogFooter className="!mt-6">
            <Button type="button" variant="outline" disabled={sending} onClick={() => onOpenChange(false)} data-testid="send-comp-audit-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={sending} data-testid="send-comp-audit-submit">
              {sending ? 'Sending…' : 'Send magic link'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
